import { useState, useRef, useEffect } from "react";
import { PrimaryButton, Checkbox } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import styles from "./TermsAndConditions.module.css";
import abbLogo from "../../assets/abb-logo.svg";

interface Props {
    onAccept: () => Promise<void>;
}

export const TermsAndConditions = ({ onAccept }: Props) => {
    const { t } = useTranslation();
    const [accepted, setAccepted] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    const checkIfScrollable = () => {
        const el = scrollRef.current;
        if (!el) return;
        // If content fits entirely (no scrollbar needed), activate the checkbox immediately
        if (el.scrollHeight <= el.clientHeight) {
            setHasScrolled(true);
        }
    };

    useEffect(() => {
        checkIfScrollable();
        const el = scrollRef.current;
        if (!el) return;
        const observer = new ResizeObserver(checkIfScrollable);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const handleScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
        if (atBottom) setHasScrolled(true);
    };

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.branding}>
                    <img src={abbLogo} alt="ABB logo" className={styles.logo} />
                    <span className={styles.appName}>{t("appTitle")}</span>
                </div>

                <h1 className={styles.title}>{t("termsAndConditions.title")}</h1>
                <p className={styles.subtitle}>{t("termsAndConditions.subtitle")}</p>

                <div className={styles.scrollArea} ref={scrollRef} onScroll={handleScroll}>
                    <h2>{t("termsAndConditions.section1Title")}</h2>
                    <p>{t("termsAndConditions.section1Body")}</p>

                    <h2>{t("termsAndConditions.section2Title")}</h2>
                    <p>{t("termsAndConditions.section2Body")}</p>

                    <h2>{t("termsAndConditions.section3Title")}</h2>
                    <p>{t("termsAndConditions.section3Body")}</p>

                    <h2>{t("termsAndConditions.section4Title")}</h2>
                    <p>{t("termsAndConditions.section4Body")}</p>

                    <h2>{t("termsAndConditions.section5Title")}</h2>
                    <p>{t("termsAndConditions.section5Body")}</p>

                    <h2>{t("termsAndConditions.section6Title")}</h2>
                    <p>{t("termsAndConditions.section6Body")}</p>

                    <h2>{t("termsAndConditions.section7Title")}</h2>
                    <p>{t("termsAndConditions.section7Body")}</p>
                </div>

                <div className={styles.checkboxWrapper}>
                    <Checkbox
                        label={t("termsAndConditions.checkboxLabel")}
                        checked={accepted}
                        disabled={!hasScrolled}
                        onChange={(_, checked) => setAccepted(!!checked)}
                    />
                    {!hasScrolled && (
                        <p className={styles.scrollHint}>{t("termsAndConditions.scrollHint")}</p>
                    )}
                </div>

                <div className={styles.buttonRow}>
                    <PrimaryButton text={t("termsAndConditions.accept")} disabled={!accepted} onClick={onAccept} />
                </div>
            </div>
        </div>
    );
};
